import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CheckCircle, Users, Calendar, TrendingUp, DollarSign, Shield, Clock, Zap, Award, BarChart3, Smartphone } from 'lucide-react';
import Button from '../../components/ui/Button';
import AppImage from '../../components/AppImage';
import { detectUserCountry, getRegionalPricing } from '../../services/geolocationService';
import { trackEvent, trackConversion } from '../../hooks/useGoogleAnalytics';
import { initScrollDepthTracking, trackTimeOnPage } from '../../utils/conversionTracking';

const MyDojoLandingPage = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);
  const [userCountry, setUserCountry] = useState(null);

  const isBrazil = userCountry === 'BR';

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const geoData = await detectUserCountry();
        setUserCountry(geoData?.country);
        const regionalPricing = getRegionalPricing(geoData?.country);
        setPricing(regionalPricing);
      } catch (err) {
        console.error('Failed to load pricing:', err);
        const fallbackPricing = getRegionalPricing('US');
        setPricing(fallbackPricing);
      } finally {
        setIsLoadingPricing(false);
      }
    };

    loadPricing();
  }, []);

  // Initialize conversion tracking
  useEffect(() => {
    const cleanupScroll = initScrollDepthTracking();
    const cleanupTime = trackTimeOnPage();

    return () => {
      if (cleanupScroll) cleanupScroll();
      if (cleanupTime) cleanupTime();
    };
  }, []);

  // Bilingual content
  const content = {
    hero: {
      title: isBrazil 
        ? 'Gestão Completa para Academias de Jiu-Jitsu'
        : 'Complete Management for Jiu-Jitsu Academies',
      subtitle: isBrazil
        ? 'Automatize alunos, presença e pagamentos. Criado por praticantes de BJJ para academias que querem crescer.'
        : 'Automate students, attendance and payments. Built by BJJ practitioners for academies that want to grow.',
      ctaPrimary: isBrazil ? 'Começar Agora - 60 Dias Grátis' : 'Get Started - 60 Days Free',
      ctaSecondary: isBrazil ? 'Ver Demonstração' : 'Watch Demo'
    },
    features: {
      title: isBrazil ? 'Tudo que Sua Academia Precisa' : 'Everything Your Academy Needs',
      subtitle: isBrazil 
        ? 'Sistema completo desenvolvido especificamente para Brazilian Jiu-Jitsu'
        : 'Complete system developed specifically for Brazilian Jiu-Jitsu',
      items: [
        {
          icon: Users,
          title: isBrazil ? 'Gestão de Alunos' : 'Student Management',
          description: isBrazil 
            ? 'Cadastro completo, faixas IBJJF, histórico de treinos e progressão técnica'
            : 'Complete registration, IBJJF belts, training history and technical progression'
        },
        {
          icon: Calendar,
          title: isBrazil ? 'Controle de Presença' : 'Attendance Tracking',
          description: isBrazil
            ? 'Check-in por QR Code, presença automática e alertas de pontualidade' :'QR Code check-in, automatic attendance and punctuality alerts'
        },
        {
          icon: DollarSign,
          title: isBrazil ? 'Pagamentos Automatizados' : 'Automated Payments',
          description: isBrazil
            ? 'PIX, Boleto e Cartão. Cobranças automáticas e notificações de vencimento'
            : 'Credit card and bank transfers. Automatic billing and payment reminders'
        },
        {
          icon: TrendingUp,
          title: isBrazil ? 'Progressão de Faixas' : 'Belt Progression',
          description: isBrazil
            ? 'Sistema IBJJF completo, elegibilidade automática e histórico de graduações'
            : 'Complete IBJJF system, automatic eligibility and graduation history'
        },
        {
          icon: BarChart3,
          title: isBrazil ? 'Análises e Relatórios' : 'Analytics & Reports',
          description: isBrazil
            ? 'Dashboard financeiro, métricas de retenção e insights de crescimento' :'Financial dashboard, retention metrics and growth insights'
        },
        {
          icon: Smartphone,
          title: isBrazil ? 'Portal do Aluno' : 'Student Portal',
          description: isBrazil
            ? 'App mobile para alunos acompanharem treinos, pagamentos e evolução' :'Mobile app for students to track training, payments and progress'
        }
      ]
    },
    benefits: {
      title: isBrazil ? 'Por Que MyDojo?' : 'Why MyDojo?',
      items: [
        {
          icon: Shield,
          title: isBrazil ? 'Específico para BJJ' : 'BJJ-Specific',
          description: isBrazil
            ? 'Único sistema que entende faixas IBJJF, terminologia e cultura do Jiu-Jitsu'
            : 'Only system that understands IBJJF belts, terminology and Jiu-Jitsu culture'
        },
        {
          icon: Zap,
          title: isBrazil ? 'IA Integrada' : 'Integrated AI',
          description: isBrazil
            ? 'Assistente inteligente que alerta sobre problemas e sugere ações' :'Smart assistant that alerts about issues and suggests actions'
        },
        {
          icon: Clock,
          title: isBrazil ? 'Setup em 5 Minutos' : '5-Minute Setup',
          description: isBrazil
            ? 'Comece a usar hoje. Sem instalação, sem complicação' :'Start using today. No installation, no hassle'
        },
        {
          icon: Award,
          title: isBrazil ? '70% Mais Barato' : '70% Cheaper',
          description: isBrazil
            ? 'Preço justo comparado a Mindbody, Kicksite e outros importados'
            : 'Fair pricing compared to Mindbody, Kicksite and other imported systems'
        }
      ]
    },
    testimonials: {
      title: isBrazil ? 'Academias Que Confiam no MyDojo' : 'Academies That Trust MyDojo',
      items: [
        {
          quote: isBrazil
            ? 'Antes eu perdia horas com planilhas. Agora o MyDojo cuida de tudo e eu foco no que importa: ensinar Jiu-Jitsu.'
            : 'Before I wasted hours on spreadsheets. Now MyDojo handles everything and I focus on what matters: teaching Jiu-Jitsu.',
          author: 'Professor Carlos Silva',
          academy: 'Gracie Barra São Paulo',
          belt: isBrazil ? 'Faixa Preta' : 'Black Belt'
        },
        {
          quote: isBrazil
            ? 'A IA me alertou sobre 3 alunos em risco de cancelamento. Consegui conversar com eles antes e todos ficaram.'
            : 'The AI alerted me about 3 students at risk of canceling. I talked to them first and they all stayed.',
          author: 'Renata Oliveira',
          academy: 'Alliance Rio de Janeiro',
          belt: isBrazil ? 'Faixa Preta' : 'Black Belt'
        },
        {
          quote: isBrazil
            ? 'Economizei R$ 400/mês trocando do Mindbody. O MyDojo tem tudo que eu preciso e custa muito menos.'
            : 'Saved $150/month switching from Mindbody. MyDojo has everything I need and costs much less.',
          author: 'João Mendes',
          academy: 'CheckMat Brasília',
          belt: isBrazil ? 'Faixa Marrom' : 'Brown Belt'
        }
      ]
    },
    pricing: {
      title: isBrazil ? 'Planos Transparentes' : 'Transparent Pricing',
      subtitle: isBrazil
        ? 'Comece gratis por 60 dias. Sem taxas ocultas. Cancele quando quiser.'
        : 'Start free for 60 days. No hidden fees. Cancel anytime.',
      trial: isBrazil ? '60 dias grátis' : '60 days free',
      noFees: isBrazil ? 'Sem taxas de setup' : 'No setup fees',
      autoUpgrade: isBrazil ? 'Upgrade automático' : 'Automatic upgrade',
      perMonth: isBrazil ? '/mês' : '/month',
      upTo: isBrazil ? 'Até' : 'Up to'
    },
    cta: {
      title: isBrazil ? 'Pronto Para Transformar Sua Academia?' : 'Ready to Transform Your Academy?',
      subtitle: isBrazil
        ? 'Junte-se a centenas de academias que já automatizaram sua gestão' :'Join hundreds of academies that have already automated their management',
      button: isBrazil ? 'Cadastrar Minha Academia' : 'Register My Academy',
      guarantee: isBrazil ? '60 dias grátis • Sem cartão de crédito' : '60 days free • No credit card required'
    }
  };

  const handleStartNow = () => {
    trackConversion('start_trial', 0);
    trackEvent('cta_click', {
      button_id: 'start_now_hero',
      page: '/'
    });
    navigate('/academy-registration');
  };

  const handleWatchDemo = () => {
    trackEvent('cta_click', {
      button_id: 'watch_demo',
      page: '/'
    });
    navigate('/landing');
  };

  const metaTitle = isBrazil 
    ? 'MyDojo - Gestão Completa para Academias de Jiu-Jitsu | Software BJJ'
    : 'MyDojo - Complete Management for Jiu-Jitsu Academies | BJJ Software';
  
  const metaDescription = isBrazil
    ? 'Automatize alunos, presença e pagamentos da sua academia de BJJ. Sistema com faixas IBJJF, IA integrada e pagamentos localizados (PIX, Boleto). 60 dias grátis.'
    : 'Automate students, attendance, and payments for your BJJ academy. IBJJF belt system, AI-powered analytics, and localized payments. 60-day free trial.';

  const testimonials = [
    {
      name: isBrazil ? 'Professor Carlos Silva' : 'Professor Carlos Silva',
      academy: isBrazil ? 'Academia Gracie São Paulo' : 'Gracie Academy São Paulo',
      text: isBrazil 
        ? 'MyDojo transformou a gestão da nossa academia. Economizamos 15 horas por semana em tarefas administrativas.'
        : 'MyDojo transformed our academy management. We save 15 hours per week on administrative tasks.',
      belt: 'Faixa Preta 3º Grau'
    },
    {
      name: isBrazil ? 'Ana Rodrigues' : 'Ana Rodrigues',
      academy: isBrazil ? 'BJJ Evolution Rio' : 'BJJ Evolution Rio',
      text: isBrazil
        ? 'O sistema de pagamentos automáticos aumentou nossa taxa de recebimento em 40%. Recomendo!'
        : 'The automated payment system increased our collection rate by 40%. Highly recommend!',
      belt: 'Faixa Preta'
    },
    {
      name: isBrazil ? 'Rafael Costa' : 'Rafael Costa',
      academy: isBrazil ? 'Team Costa Jiu-Jitsu' : 'Team Costa Jiu-Jitsu',
      text: isBrazil
        ? 'Melhor investimento que fizemos. O controle de presença e progressão de faixas é impecável.' :'Best investment we made. Attendance tracking and belt progression is flawless.',
      belt: 'Faixa Preta 2º Grau'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mydojo.info" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href="https://mydojo.info" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900">
        {/* Hero Section */}
        <section className="relative px-4 py-16 md:py-24">
          <div className="max-w-6xl mx-auto text-center">
            {/* Logo */}
            <div className="mb-8 animate-fade-in">
              <AppImage
                src="/assets/images/big_logo_icon_white-1767413559816.png"
                alt="MyDojo Logo - Brazilian Jiu-Jitsu Academy Management Platform"
                className="h-24 w-auto md:h-32 mx-auto object-contain"
              />
            </div>

            {/* Hero Title */}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {content?.hero?.title}
            </h1>
            <p className="text-lg md:text-xl text-[#AAAAAA] mb-10 max-w-3xl mx-auto">
              {content?.hero?.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Button
                onClick={handleStartNow}
                size="lg"
                className="bg-[#B22222] hover:bg-[#8B1A1A] text-white font-semibold px-8 py-4 text-lg shadow-xl hover:shadow-2xl transition-all"
              >
                {content?.hero?.ctaPrimary}
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 text-[#AAAAAA] text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-gray-400" />
                <span>{content?.pricing?.trial}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-gray-400" />
                <span>{content?.pricing?.noFees}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-gray-400" />
                <span>{content?.pricing?.autoUpgrade}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="px-4 py-16 bg-[#000000]/50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {content?.features?.title}
              </h2>
              <p className="text-lg text-[#AAAAAA] max-w-2xl mx-auto">
                {content?.features?.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content?.features?.items?.map((feature, index) => {
                const IconComponent = feature?.icon;
                return (
                  <div
                    key={index}
                    className="bg-[#555555]/20 border border-[#555555] rounded-xl p-6 hover:border-[#B22222] transition-all hover:transform hover:scale-105"
                  >
                    <div className="bg-[#B22222]/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-[#B22222]" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {feature?.title}
                    </h3>
                    <p className="text-[#AAAAAA] leading-relaxed">
                      {feature?.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              {content?.benefits?.title}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {content?.benefits?.items?.map((benefit, index) => {
                const IconComponent = benefit?.icon;
                return (
                  <div
                    key={index}
                    className="bg-[#555555]/20 border border-[#555555] rounded-xl p-6 text-center hover:border-[#B22222] transition-all"
                  >
                    <div className="bg-[#B22222]/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-[#B22222]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {benefit?.title}
                    </h3>
                    <p className="text-[#AAAAAA] text-sm leading-relaxed">
                      {benefit?.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Testimonials Section - Social Proof */}
        <section className="px-4 py-16 bg-[#000000]/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
              {isBrazil ? 'O Que Professores Dizem' : 'What Professors Say'}
            </h2>
            <p className="text-[#AAAAAA] text-center mb-12">
              {isBrazil ? 'Academias reais usando MyDojo todos os dias' : 'Real academies using MyDojo every day'}
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials?.map((testimonial, index) => (
                <div key={index} className="bg-[#1A1A1A] border border-[#333333] rounded-lg p-6 hover:border-[#B22222] transition-all">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-[#B22222] rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {testimonial?.name?.charAt(0)}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-white font-semibold">{testimonial?.name}</h3>
                      <p className="text-[#AAAAAA] text-sm">{testimonial?.belt}</p>
                    </div>
                  </div>
                  <p className="text-[#CCCCCC] mb-4 italic">"{testimonial?.text}"</p>
                  <p className="text-[#B22222] text-sm font-semibold">{testimonial?.academy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        {!isLoadingPricing && pricing && (
          <section className="px-4 py-16">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {content?.pricing?.title}
                </h2>
                <p className="text-lg text-[#AAAAAA]">
                  {content?.pricing?.subtitle}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Basic Plan */}
                <div className="bg-[#555555]/20 border-2 border-gray-500 rounded-xl p-6 hover:border-gray-400 transition-all">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">⚪</span>
                    <h3 className="text-xl font-bold text-white">{isBrazil ? 'Básico' : 'Basic'}</h3>
                  </div>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">{pricing?.basic?.formatted}</span>
                    <span className="text-[#AAAAAA] ml-2">{content?.pricing?.perMonth}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#AAAAAA] mb-6">
                    <CheckCircle className="w-5 h-5 text-gray-500" />
                    <span>{content?.pricing?.upTo} {pricing?.basic?.limit}</span>
                  </div>
                </div>

                {/* Pro Plan */}
                <div className="bg-[#555555]/20 border-2 border-yellow-500 rounded-xl p-6 hover:border-yellow-400 transition-all transform md:scale-105">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">🟡</span>
                    <h3 className="text-xl font-bold text-white">Pro</h3>
                  </div>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">{pricing?.pro?.formatted}</span>
                    <span className="text-[#AAAAAA] ml-2">{content?.pricing?.perMonth}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#AAAAAA] mb-6">
                    <CheckCircle className="w-5 h-5 text-yellow-500" />
                    <span>{content?.pricing?.upTo} {pricing?.pro?.limit}</span>
                  </div>
                </div>

                {/* Unlimited Plan */}
                <div className="bg-[#555555]/20 border-2 border-red-500 rounded-xl p-6 hover:border-red-400 transition-all">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">🔴</span>
                    <h3 className="text-xl font-bold text-white">Unlimited</h3>
                  </div>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">{pricing?.unlimited?.formatted}</span>
                    <span className="text-[#AAAAAA] ml-2">{content?.pricing?.perMonth}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#AAAAAA] mb-6">
                    <CheckCircle className="w-5 h-5 text-red-500" />
                    <span>{pricing?.unlimited?.limit}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Final CTA Section */}
        <section className="px-4 py-16 bg-[#000000]/50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              {content?.cta?.title}
            </h2>
            <p className="text-lg text-[#AAAAAA] mb-8">
              {content?.cta?.subtitle}
            </p>
            <Button
              onClick={handleStartNow}
              size="xl"
              className="bg-[#B22222] hover:bg-[#8B1A1A] text-white font-semibold px-12 py-5 text-xl shadow-2xl hover:shadow-3xl transition-all"
            >
              {content?.cta?.button}
            </Button>
            <p className="text-[#AAAAAA] text-sm mt-6">
              {content?.cta?.guarantee}
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-4 py-8 border-t border-[#555555]">
          <div className="max-w-6xl mx-auto">
            {/* Contact Information */}
            <div className="grid md:grid-cols-3 gap-8 mb-8 text-center md:text-left">
              <div>
                <h3 className="text-white font-semibold mb-3">
                  {isBrazil ? 'Contato' : 'Contact'}
                </h3>
                <p className="text-[#AAAAAA] text-sm mb-2">
                  <a href="mailto:support@mydojo.info" className="hover:text-white transition-colors">
                    support@mydojo.info
                  </a>
                </p>
                <p className="text-[#AAAAAA] text-sm">
                  {isBrazil ? 'Suporte: Seg-Sex, 9h-18h BRT' : 'Support: Mon-Fri, 9am-6pm BRT'}
                </p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-3">
                  {isBrazil ? 'Empresa' : 'Company'}
                </h3>
                <p className="text-[#AAAAAA] text-sm mb-2">
                  Sinistro Photo Film & Design Inc.
                </p>
                <p className="text-[#AAAAAA] text-sm">
                  {isBrazil ? 'Feito por praticantes de BJJ' : 'Built by BJJ practitioners'}
                </p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-3">
                  {isBrazil ? 'Recursos' : 'Resources'}
                </h3>
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => navigate('/help-center-manual')}
                    className="text-[#AAAAAA] text-sm hover:text-white transition-colors"
                  >
                    {isBrazil ? 'Central de Ajuda' : 'Help Center'}
                  </button>
                  <button
                    onClick={() => window.open('https://wa.me/message/YOUR_WHATSAPP', '_blank')}
                    className="text-[#AAAAAA] text-sm hover:text-white transition-colors"
                  >
                    {isBrazil ? 'WhatsApp Suporte' : 'WhatsApp Support'}
                  </button>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-center text-[#AAAAAA] text-sm border-t border-[#333333] pt-6">
              <p>© 2026 MyDojo. A product of Sinistro Photo Film & Design Inc.</p>
              <p className="mt-1">{isBrazil ? 'Todos os direitos reservados.' : 'All rights reserved.'}</p>
              <div className="mt-4 flex items-center justify-center space-x-4">
                <button
                  onClick={() => navigate('/terms-of-service')}
                  className="hover:text-white transition-colors"
                >
                  {isBrazil ? 'Termos de Serviço' : 'Terms of Service'}
                </button>
                <span>•</span>
                <button
                  onClick={() => navigate('/privacy-policy')}
                  className="hover:text-white transition-colors"
                >
                  {isBrazil ? 'Política de Privacidade' : 'Privacy Policy'}
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default MyDojoLandingPage;